import { useEffect, useState } from 'react'

import { Attribute, Status, EventTypes } from './enums'
import type { ScriptElement } from './types'

const useScript = ({
  src,
  id,
  reload = false
}: {
  src: string
  id?: string
  reload?: boolean
}): Status => {
  const [status, setStatus] = useState<Status>(src ? Status.Loading : Status.Idle)

  useEffect(() => {
    if (!src) {
      setStatus(Status.Idle)

      return
    }

    let script: ScriptElement = document.querySelector(`script[src="${src}"]`)

    if (reload && script) {
      script.parentNode?.removeChild(script)
    }
    if (!script) {
      script = document.createElement('script')
      script.src = src
      script.defer = true
      script.setAttribute(Attribute.DataStatus, Status.Loading)
      if (id) {
        script.id = id
      }

      document.body.appendChild(script)

      const setAttributeFromEvent = (event: Event) => {
        script?.setAttribute(
          Attribute.DataStatus,
          event.type === EventTypes.Load ? Status.Ready : Status.Error
        )
      }

      script.addEventListener(EventTypes.Load, setAttributeFromEvent)
      script.addEventListener(EventTypes.Error, setAttributeFromEvent)
    } else {
      setStatus(script.getAttribute(Attribute.DataStatus) as Status)
    }

    const setStateFromEvent = (event: Event) => {
      setStatus(event.type === EventTypes.Load ? Status.Ready : Status.Error)
    }

    script.addEventListener(EventTypes.Load, setStateFromEvent)
    script.addEventListener(EventTypes.Error, setStateFromEvent)

    return () => {
      if (script) {
        script.removeEventListener(EventTypes.Load, setStateFromEvent)
        script.removeEventListener(EventTypes.Error, setStateFromEvent)
      }
    }
  }, [id, reload, src])

  return status
}

export default useScript
